import apiClient from './client';
import type {
  SignupRequest,
  SignupResponse,
  TrialConfig,
  PromoCodeValidationRequest,
  PromoCodeValidationResponse,
  OnboardingStatusResponse,
  TrialSetupResponse,
  CompleteOnboardingResponse,
  EmailVerificationStatusResponse,
  EmailVerificationResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
} from '@/types/signup';

export const signupApi = {
  /**
   * Get current trial configuration
   */
  async getTrialConfig(): Promise<TrialConfig> {
    const response = await apiClient.get<TrialConfig>('/onboarding/trial-config');
    return response.data;
  },

  /**
   * Validate a promo code
   */
  async validatePromoCode(
    request: PromoCodeValidationRequest
  ): Promise<PromoCodeValidationResponse> {
    const response = await apiClient.post<PromoCodeValidationResponse>(
      '/onboarding/validate-promo-code',
      request
    );
    return response.data;
  },

  /**
   * Create a new user account
   */
  async signup(data: SignupRequest): Promise<SignupResponse> {
    const response = await apiClient.post<SignupResponse>('/onboarding/signup', data);
    return response.data;
  },

  /**
   * Get onboarding status for a member
   */
  async getOnboardingStatus(memberUuid: string): Promise<OnboardingStatusResponse> {
    const response = await apiClient.get<OnboardingStatusResponse>(
      `/onboarding/status/${memberUuid}`
    );
    return response.data;
  },

  /**
   * Setup trial (for MVP free trial)
   */
  async setupTrial(memberUuid: string): Promise<TrialSetupResponse> {
    const response = await apiClient.post<TrialSetupResponse>(
      `/onboarding/trial-setup/${memberUuid}`
    );
    return response.data;
  },

  /**
   * Complete onboarding and get auth tokens
   */
  async completeOnboarding(memberUuid: string): Promise<CompleteOnboardingResponse> {
    const response = await apiClient.post<CompleteOnboardingResponse>(
      `/onboarding/complete/${memberUuid}`
    );
    return response.data;
  },

  /**
   * Check email verification status
   */
  async checkEmailVerificationStatus(
    memberUuid: string
  ): Promise<EmailVerificationStatusResponse> {
    const response = await apiClient.get<EmailVerificationStatusResponse>(
      `/onboarding/email-verification-status/${memberUuid}`
    );
    return response.data;
  },

  /**
   * Verify email with token (called when user clicks email link)
   */
  async verifyEmail(token: string): Promise<EmailVerificationResponse> {
    const response = await apiClient.get<EmailVerificationResponse>(
      `/onboarding/verify-email?token=${token}`
    );
    return response.data;
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail(
    request: ResendVerificationRequest
  ): Promise<ResendVerificationResponse> {
    const response = await apiClient.post<ResendVerificationResponse>(
      '/onboarding/resend-verification-email',
      request
    );
    return response.data;
  },
};
